<template>
  <div class="cc-table-wrap">
    <div class="cc-toolbar">
      <div class="cc-toolbar__actions">
        <button
          type="button"
          class="cc-btn cc-btn--ghost"
          :disabled="disabled"
          @click="$emit('add-row')"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
          />
          {{ $t('convertCoordinates.actions.addRow') }}
        </button>
        <button
          type="button"
          class="cc-btn cc-btn--ghost"
          :disabled="disabled"
          @click="pasteClipboard"
        >
          <feather-icon
            icon="ClipboardIcon"
            size="14"
          />
          {{ $t('convertCoordinates.actions.paste') }}
        </button>
        <button
          type="button"
          class="cc-btn cc-btn--ghost"
          :disabled="disabled"
          @click="$emit('clear')"
        >
          <feather-icon
            icon="Trash2Icon"
            size="14"
          />
          {{ $t('convertCoordinates.actions.clear') }}
        </button>
      </div>
      <span class="cc-count">{{ rows.length }} / {{ maxPoints }}</span>
    </div>

    <div
      class="cc-table-scroll"
      @paste="onPaste"
    >
      <table class="cc-table">
        <thead>
          <tr>
            <th class="cc-th-index">#</th>
            <th>{{ $t('convertCoordinates.fields.name') }}</th>
            <th
              v-if="showWgs84"
              colspan="2"
            >
              {{ $t('convertCoordinates.groups.wgs84') }}
            </th>
            <th
              v-if="showUtm"
              colspan="3"
            >
              {{ $t('convertCoordinates.groups.utm') }}
            </th>
            <th
              v-if="showEtm"
              colspan="3"
            >
              {{ $t('convertCoordinates.groups.etm') }}
            </th>
            <th
              v-if="showDms"
              colspan="2"
            >
              {{ $t('convertCoordinates.groups.dms') }}
            </th>
            <th
              v-if="showXy"
              colspan="2"
            >
              {{ $t('convertCoordinates.groups.xy') }}
            </th>
            <th class="cc-th-action" />
          </tr>
          <tr class="cc-subhead">
            <th />
            <th />
            <template v-if="showWgs84">
              <th>{{ $t('convertCoordinates.fields.latitude') }}</th>
              <th>{{ $t('convertCoordinates.fields.longitude') }}</th>
            </template>
            <template v-if="showUtm">
              <th>{{ $t('convertCoordinates.fields.utmEast') }}</th>
              <th>{{ $t('convertCoordinates.fields.utmNorth') }}</th>
              <th>{{ $t('convertCoordinates.fields.utmZone') }}</th>
            </template>
            <template v-if="showEtm">
              <th>{{ $t('convertCoordinates.fields.etmEast') }}</th>
              <th>{{ $t('convertCoordinates.fields.etmNorth') }}</th>
              <th>{{ $t('convertCoordinates.fields.etmZone') }}</th>
            </template>
            <template v-if="showDms">
              <th>{{ $t('convertCoordinates.fields.dmsLatitude') }}</th>
              <th>{{ $t('convertCoordinates.fields.dmsLongitude') }}</th>
            </template>
            <template v-if="showXy">
              <th>X</th>
              <th>Y</th>
            </template>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="row._id"
            :class="{ 'is-invalid': invalidIndexes.indexOf(index + 1) !== -1 }"
          >
            <td class="cc-td-index">
              {{ index + 1 }}
            </td>
            <td>
              <input
                v-model="row.name"
                class="cc-input cc-input--table"
                type="text"
                :disabled="disabled"
                :placeholder="$t('convertCoordinates.fields.name')"
              >
            </td>
            <template v-if="showWgs84">
              <td>
                <input
                  v-model="row.latitude"
                  class="cc-input cc-input--table"
                  type="text"
                  inputmode="decimal"
                  :disabled="disabled"
                  placeholder="27.18"
                >
              </td>
              <td>
                <input
                  v-model="row.longitude"
                  class="cc-input cc-input--table"
                  type="text"
                  inputmode="decimal"
                  :disabled="disabled"
                  placeholder="31.18"
                >
              </td>
            </template>
            <template v-if="showUtm">
              <td>
                <input
                  v-model="row.utm_east"
                  class="cc-input cc-input--table"
                  type="text"
                  inputmode="decimal"
                  :disabled="disabled"
                  placeholder="311554.19"
                >
              </td>
              <td>
                <input
                  v-model="row.utm_north"
                  class="cc-input cc-input--table"
                  type="text"
                  inputmode="decimal"
                  :disabled="disabled"
                  placeholder="2993394.50"
                >
              </td>
              <td>
                <input
                  v-model="row.utm_zone"
                  class="cc-input cc-input--table"
                  type="text"
                  :disabled="disabled"
                  :placeholder="utmZone || '36N'"
                >
              </td>
            </template>
            <template v-if="showEtm">
              <td>
                <input
                  v-model="row.etm_east"
                  class="cc-input cc-input--table"
                  type="text"
                  inputmode="decimal"
                  :disabled="disabled"
                >
              </td>
              <td>
                <input
                  v-model="row.etm_north"
                  class="cc-input cc-input--table"
                  type="text"
                  inputmode="decimal"
                  :disabled="disabled"
                >
              </td>
              <td>
                <input
                  v-model="row.etm_zone"
                  class="cc-input cc-input--table"
                  type="text"
                  :disabled="disabled"
                >
              </td>
            </template>
            <template v-if="showDms">
              <td>
                <input
                  v-model="row.dms_latitude"
                  class="cc-input cc-input--table"
                  type="text"
                  :disabled="disabled"
                  placeholder="27°10'48.00&quot;N"
                >
              </td>
              <td>
                <input
                  v-model="row.dms_longitude"
                  class="cc-input cc-input--table"
                  type="text"
                  :disabled="disabled"
                  placeholder="31°10'48.00&quot;E"
                >
              </td>
            </template>
            <template v-if="showXy">
              <td>
                <input
                  v-model="row.x"
                  class="cc-input cc-input--table"
                  type="text"
                  inputmode="decimal"
                  :disabled="disabled"
                >
              </td>
              <td>
                <input
                  v-model="row.y"
                  class="cc-input cc-input--table"
                  type="text"
                  inputmode="decimal"
                  :disabled="disabled"
                >
              </td>
            </template>
            <td class="cc-td-action">
              <button
                type="button"
                class="cc-icon-btn"
                :disabled="disabled || rows.length === 1"
                :title="$t('convertCoordinates.actions.removeRow')"
                @click="$emit('remove-row', index)"
              >
                <feather-icon
                  icon="XIcon"
                  size="14"
                />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="cc-hint">
      {{ $t('convertCoordinates.tableHint') }}
    </p>
  </div>
</template>

<script>
import { MAX_COORDINATES } from '@/api/coordinate-conversion/types'

export default {
  name: 'CoordinateInputTable',
  props: {
    rows: {
      type: Array,
      required: true,
    },
    sourceSystem: {
      type: String,
      default: 'auto',
    },
    utmZone: {
      type: String,
      default: '',
    },
    invalidIndexes: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      maxPoints: MAX_COORDINATES,
    }
  },
  computed: {
    showWgs84() {
      return this.sourceSystem === 'auto' || this.sourceSystem === 'wgs84'
    },
    showUtm() {
      return this.sourceSystem === 'auto' || this.sourceSystem === 'utm'
    },
    showEtm() {
      return this.sourceSystem === 'auto' || this.sourceSystem === 'etm'
    },
    showDms() {
      return this.sourceSystem === 'auto' || this.sourceSystem === 'dms'
    },
    showXy() {
      return this.sourceSystem === 'auto'
    },
  },
  methods: {
    async pasteClipboard() {
      if (this.disabled) return
      try {
        if (!navigator.clipboard || !navigator.clipboard.readText) {
          this.$toast.error(this.$t('convertCoordinates.errors.clipboardUnsupported'))
          return
        }
        const text = await navigator.clipboard.readText()
        this.$emit('paste', text)
      } catch (err) {
        this.$toast.error(this.$t('convertCoordinates.errors.clipboardDenied'))
      }
    },
    onPaste(event) {
      const text = event.clipboardData && event.clipboardData.getData('text')
      if (!text || !text.includes('\n') && !text.includes('\t')) return
      event.preventDefault()
      this.$emit('paste', text)
    },
  },
}
</script>
